import { CommonModule } from '@angular/common';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { RouterLink } from '@angular/router';
import { SubSink } from 'subsink';

interface ReleaseItem {
  title: string;
  description: string;
  pubDate: Date;
  link: string;
  version?: string;
}

@Component({
  selector: 'app-releases-card',
  standalone: true,
  imports: [CommonModule, RouterLink, HttpClientModule],
  template: `
    <div class="releases-content">
      <div *ngIf="loading" class="releases-loading">
        <i class="pi pi-spin pi-spinner"></i>
        <span>Loading releases...</span>
      </div>

      <div *ngIf="error && !loading" class="releases-error">
        <i class="pi pi-info-circle"></i>
        <span>Unable to load release notes.</span>
        <a href="https://moveright.app/releases/" target="_blank" rel="noopener">View online</a>
      </div>

      <div *ngIf="!loading && !error && releases.length === 0" class="releases-empty">
        <span>No releases published yet.</span>
      </div>

      <div *ngIf="!loading && !error && releases.length > 0" class="releases-list">
        <a
          *ngFor="let release of releases.slice(0, 3)"
          class="release-item"
          [href]="release.link"
          target="_blank"
          rel="noopener"
        >
          <div class="release-item-header">
            <span class="release-version">{{ release.title }}</span>
            <span class="release-date">{{ release.pubDate | date:'MMM d, y' }}</span>
          </div>
          <p *ngIf="release.description" class="release-description">
            {{ release.description }}
          </p>
        </a>
      </div>

      <a
        *ngIf="!loading && !error && releases.length > 0"
        class="releases-view-all"
        href="https://moveright.app/releases/"
        target="_blank"
        rel="noopener"
      >
        View all releases
        <i class="pi pi-arrow-right"></i>
      </a>
    </div>
  `,
  styles: [`
    .releases-content {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }
    .releases-loading, .releases-error, .releases-empty {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 0;
      color: var(--text-color-secondary);
      font-size: 0.875rem;
    }
    .releases-error {
      flex-direction: column;
      gap: 0.25rem;
      text-align: center;
    }
    .releases-error a {
      color: var(--color-primary, var(--primary-color));
      font-size: 0.8125rem;
    }
    .releases-list {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }
    .release-item {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      padding: 0.625rem 0.875rem;
      border-radius: 6px;
      text-decoration: none;
      color: var(--text-color);
      transition: background 0.15s;
    }
    .release-item:hover {
      background: var(--surface-hover);
    }
    .release-item-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
    }
    .release-version {
      font-weight: 600;
      font-size: 0.875rem;
    }
    .release-date {
      font-size: 0.75rem;
      color: var(--text-color-secondary);
      white-space: nowrap;
    }
    .release-description {
      font-size: 0.8125rem;
      color: var(--text-color-secondary);
      line-height: 1.4;
      margin: 0;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }
    .releases-view-all {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.5rem 0.875rem;
      font-size: 0.8125rem;
      color: var(--color-primary, var(--primary-color));
      text-decoration: none;
    }
    .releases-view-all:hover {
      text-decoration: underline;
    }
  `],
})
export class ReleasesCardComponent implements OnInit, OnDestroy {

  releases: ReleaseItem[] = [];

  loading = true;

  error = false;

  private subs = new SubSink();

  private feedUrl = 'https://moveright.app/releases/rss.xml';

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.subs.sink = this.http
      .get(this.feedUrl, { responseType: 'text' })
      .subscribe({
        next: (xml) => {
          this.releases = this.parseRss(xml);
          this.loading = false;
        },
        error: () => {
          this.error = true;
          this.loading = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private parseRss(xml: string): ReleaseItem[] {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, 'text/xml');
    const items = Array.from(doc.querySelectorAll('item'));

    return items.map((item) => {
      const title = item.querySelector('title')?.textContent?.trim() ?? '';
      const description = item.querySelector('description')?.textContent?.trim() ?? '';
      const pubDateText = item.querySelector('pubDate')?.textContent?.trim() ?? '';
      const link = item.querySelector('link')?.textContent?.trim() ?? '';
      const pubDate = pubDateText ? new Date(pubDateText) : new Date();

      return { title, description, pubDate, link };
    });
  }
}